import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { MODULES } from "../lib/modules";
import { readPrefilter, hasPrefilter, applyPrefilter } from "../lib/prefilter";
import { exportCSV } from "../lib/export";
import FilterBanner from "./FilterBanner";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Download, Sparkles, Loader2, RefreshCw, Paperclip, BellRing, ShieldCheck, BarChart3 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import DocActions, { DOC_SLOTS } from "./DocActions";
import MaintenanceDetail from "./MaintenanceDetail";

const badgeClass = {
  ok: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  danger: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  info: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  muted: "bg-muted text-muted-foreground",
};

function fmt(field, val) {
  const moneyFields = ["market_value", "monthly_rent", "amount_due", "amount_paid", "annual_premium",
    "amount", "estimated_cost", "actual_cost", "purchase_price", "rent_amount", "current_balance",
    "monthly_payment", "monthly_fee", "original_amount", "coverage_amount", "security_deposit", "escrow"];
  if (moneyFields.includes(field)) return money(val);
  if (field === "rating" && val) return `★ ${val}`;
  return val ?? "—";
}

export default function ResourcePage({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const pf = useMemo(() => readPrefilter(sp), [sp]);
  const prefilterActive = hasPrefilter(pf);
  const clearPrefilter = () => setSp({}, { replace: true });
  const [sendingReminders, setSendingReminders] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const { user, canWrite: canWriteGlobal } = useAuth();
  const canWrite = canWriteGlobal && !(user?.role === "contractor" && moduleKey !== "maintenance");
  const showReminderAction = moduleKey === "payments" && pf.overdue && canWrite;
  const sendReminders = async () => {
    if (!window.confirm("Send a rent reminder email to all tenants with unpaid or partial balances this month?")) return;
    setSendingReminders(true);
    try {
      const { data } = await api.post("/reminders/run");
      if (data.sent > 0) toast.success(`Reminders sent to ${data.sent} tenant${data.sent !== 1 ? "s" : ""}${data.failed ? `, ${data.failed} failed` : ""}.`);
      else if (data.failed > 0) toast.error(`${data.failed} reminder(s) failed. Check Rent Reminders → is Gmail connected?`);
      else toast.info("No reminders to send — all tenants are paid or excluded.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminders.");
    } finally {
      setSendingReminders(false);
    }
  };
  const reminderButton = showReminderAction ? (
    <button onClick={sendReminders} disabled={sendingReminders} data-testid="send-reminders-all-btn"
      className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 text-white px-3 py-1.5 text-xs font-medium hover:bg-amber-600 transition-colors disabled:opacity-50">
      {sendingReminders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
      {sendingReminders ? "Sending…" : "Send reminder to all overdue"}
    </button>
  ) : null;
  const relTime = (iso) => {
    if (!iso) return null;
    const diff = Date.now() - new Date(iso).getTime();
    if (isNaN(diff)) return null;
    const days = Math.floor(diff / 86400000);
    if (days <= 0) { const h = Math.floor(diff / 3600000); return h <= 0 ? "just now" : `${h}h ago`; }
    if (days === 1) return "yesterday";
    return `${days}d ago`;
  };
  const remindTenant = async (it) => {
    if (!it.tenant_id) { toast.error("No tenant linked to this record."); return; }
    setRemindingId(it.id);
    try {
      const { data } = await api.post("/reminders/run-tenant", { tenant_id: it.tenant_id });
      if (data.sent > 0) { toast.success(`Reminder sent to ${it.tenant_name || "tenant"}.`); setRemindedMap((m) => ({ ...m, [it.tenant_id]: new Date().toISOString() })); }
      else if (data.failed > 0) toast.error(`Reminder failed. Check Rent Reminders → is Gmail connected?`);
      else toast.info("No reminder needed — tenant is paid or excluded.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder.");
    } finally {
      setRemindingId(null);
    }
  };
  const hasDocs = !!DOC_SLOTS[moduleKey];
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [docCounts, setDocCounts] = useState({});
  const [remindedMap, setRemindedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [lookupAddr, setLookupAddr] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupInfo, setLookupInfo] = useState(null);
  const [rentEst, setRentEst] = useState(null);
  const [rentEstLoading, setRentEstLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${moduleKey}`);
      setItems(data);
      const refKeys = [...new Set(cfg.fields.filter((f) => f.type === "ref").map((f) => f.ref))];
      const r = {};
      await Promise.all(refKeys.map(async (rk) => {
        const res = await api.get(`/${rk}`);
        r[rk] = res.data;
      }));
      setRefs(r);
      if (moduleKey === "properties") {
        try {
          const { data: allDocs } = await api.get("/documents");
          const counts = {};
          allDocs.forEach((d) => { if (d.property_id) counts[d.property_id] = (counts[d.property_id] || 0) + 1; });
          setDocCounts(counts);
        } catch (de) { console.warn("Property document counts unavailable:", de); }
      }
      if (moduleKey === "payments") {
        try {
          const { data: comms } = await api.get("/communications");
          const map = {};
          comms.forEach((c) => {
            if (!String(c.delivery_status || "").startsWith("Sent")) return;
            const d = c.date_sent || c.created_at;
            if (c.tenant_id && d && (!map[c.tenant_id] || d > map[c.tenant_id])) map[c.tenant_id] = d;
          });
          setRemindedMap(map);
        } catch (ce) { console.warn("Reminder history unavailable:", ce); }
      }
    } catch (e) {
      console.error("ResourcePage load error:", e);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [moduleKey, cfg]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let base = applyPrefilter(items, pf);
    if (!search) return base;
    const s = search.toLowerCase();
    return base.filter((it) => JSON.stringify(it).toLowerCase().includes(s));
  }, [items, search, pf]);

  const openNew = () => { setEditing(null); setForm({}); setLookupAddr(""); setLookupInfo(null); setRentEst(null); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setLookupAddr(it.address || ""); setLookupInfo(null); setRentEst(null); setModalOpen(true); };

  const estimateRent = async () => {
    const prop = (refs.properties || []).find((p) => p.name === form.property_name);
    if (!prop || !prop.address) { toast.error("Select a property first (it needs an address)"); return; }
    setRentEstLoading(true);
    try {
      const { data } = await api.get(`/unit/rent-estimate`, { params: { address: prop.address } });
      setRentEst(data);
      setForm((p) => ({ ...p, market_rent: data.rent, market_rent_low: data.rent_low,
        market_rent_high: data.rent_high, market_rent_updated: new Date().toISOString() }));
      toast.success(`Market rent estimate: ${money(data.rent)}/mo`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRentEstLoading(false);
    }
  };

  const autoFill = async (refresh = false) => {
    const addr = (lookupAddr || form.address || "").trim();
    if (addr.length < 6) { toast.error("Enter a full street address (street, city, state)"); return; }
    setLookupLoading(true);
    try {
      const { data } = await api.get(`/property/lookup`, { params: { address: addr, refresh } });
      setForm((prev) => ({
        ...prev,
        address: data.address || addr,
        city: data.city || prev.city,
        county: data.county || prev.county,
        state: data.state || prev.state,
        zip: data.zip || prev.zip,
        parcel_id: data.parcel_id || prev.parcel_id,
        owner: data.owner_of_record || prev.owner,
        type: data.type || prev.type,
        units: data.units ?? prev.units,
        market_value: data.market_value || prev.market_value,
        market_value_source: data.market_value_source || prev.market_value_source,
        purchase_price: data.purchase_price || prev.purchase_price,
        purchase_date: data.purchase_date || prev.purchase_date,
        purchase_year: data.purchase_year || prev.purchase_year,
        photo: data.photo || prev.photo,
        notes: prev.notes ? `${prev.notes}\n\n${data.notes}` : data.notes,
      }));
      setLookupInfo(data);
      if (data.missing_fields?.length) toast.info(`Filled from public records. Missing (add manually): ${data.missing_fields.join(", ")}`);
      else toast.success("Property details filled from public records");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLookupLoading(false);
    }
  };

  // Keep property type in sync with the (editable) unit count.
  const onUnitsChange = (value) => {
    const n = Number(value) || 0;
    setForm((prev) => {
      const next = { ...prev, units: n };
      const t = (prev.type || "").toLowerCase();
      const isResidential = !["condo", "townhouse", "commercial"].includes(t);
      if (isResidential) {
        if (n === 2) next.type = "Duplex";
        else if (n === 3) next.type = "Triplex";
        else if (n === 4) next.type = "Fourplex";
        else if (n >= 5) next.type = "Multi-Family";
        else if (n === 1 && ["duplex", "triplex", "fourplex", "multi-family"].includes(t)) next.type = "Single Family";
      }
      return next;
    });
  };

  const setField = (f, value) => {
    setForm((prev) => {
      const next = { ...prev, [f.key]: value };
      if (f.type === "ref") {
        const opt = (refs[f.ref] || []).find((o) => o[f.refLabel] === value);
        if (opt) next[f.setId] = opt.id;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/${moduleKey}/${editing.id}`, form);
        toast.success(`${cfg.singular} updated`);
      } else {
        const { data: created } = await api.post(`/${moduleKey}`, form);
        toast.success(`${cfg.singular} created`);
        // Auto-generate unit records for a newly added property based on its unit count.
        if (moduleKey === "properties" && created?.id) {
          try {
            const { data: gen } = await api.post(`/properties/${created.id}/generate-units`, {
              count: Number(form.units) || 1, type: form.type || "",
            });
            if (gen.created_count > 0) toast.success(`Auto-created ${gen.created_count} unit${gen.created_count !== 1 ? "s" : ""} for this property`);
          } catch (ge) { console.warn("Auto-generate units skipped:", ge); }
        }
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete this ${cfg.singular}?`)) return;
    try {
      await api.delete(`/${moduleKey}/${it.id}`);
      toast.success(`${cfg.singular} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const columns = cfg.columns.map((c) => ({ key: c, label: cfg.labels[c] }));
    exportCSV(moduleKey, columns, filtered);
  };

  const renderBadge = (it) => {
    if (!cfg.badge) return null;
    const val = it[cfg.badge.field];
    const tone = cfg.badge.map[val] || "muted";
    return <Badge className={`rounded-sm font-normal ${badgeClass[tone]}`}>{val}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid={`page-${moduleKey}`}>
      {prefilterActive && <FilterBanner label={pf.label} count={filtered.length} onClear={clearPrefilter} action={reminderButton} />}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid={`search-${moduleKey}`}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-48 rounded-xl"
            />
          </div>
          <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid={`export-${moduleKey}`}>
            <Download className="w-4 h-4" />
          </Button>
          {canWrite && (
            <Button className="rounded-xl" onClick={openNew} data-testid={`add-${moduleKey}`}>
              <Plus className="w-4 h-4 mr-1" /> Add {cfg.singular}
            </Button>
          )}
        </div>
      </div>

      {/* Properties card grid */}
      {moduleKey === "properties" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => {
            const canOpenProfile = user?.role !== "contractor";
            return (
            <div key={p.id} onClick={canOpenProfile ? () => navigate(`/properties/${p.id}`) : undefined}
              className={`border border-border bg-card group ${canOpenProfile ? "cursor-pointer hover:border-primary/40 hover:shadow-sm transition-all" : ""}`}
              data-testid={`card-${p.id}`}>
              <div className="h-40 bg-muted overflow-hidden">
                {p.photo && <img src={p.photo} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading font-semibold">{p.name}</h3>
                  <Badge className="rounded-sm bg-primary/10 text-primary font-normal">{p.type}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{p.address}</p>
                <div className="flex items-center justify-between pt-2 text-sm">
                  <span className="text-muted-foreground">{p.units} unit{p.units !== 1 ? "s" : ""}</span>
                  <span className="font-mono font-semibold">{money(p.market_value)}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/documents?property_id=${p.id}&property_name=${encodeURIComponent(p.name)}`); }}
                  data-testid={`docs-badge-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                  title="View attached documents">
                  <Paperclip className="w-3.5 h-3.5" /> {docCounts[p.id] || 0} document{(docCounts[p.id] || 0) !== 1 ? "s" : ""}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/compliance?property_id=${p.id}`); }}
                  data-testid={`compliance-link-${p.id}`}
                  className="ml-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                  title="View compliance for this property">
                  <ShieldCheck className="w-3.5 h-3.5" /> Compliance
                </button>
                {canOpenProfile && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.id}`); }}
                    data-testid={`profile-link-${p.id}`}
                    className="ml-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                    title="View property profile & financial reports">
                    <BarChart3 className="w-3.5 h-3.5" /> Reports
                  </button>
                )}
                {canWrite && (
                  <div className="flex gap-2 pt-2 border-t border-border mt-2">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} data-testid={`edit-${p.id}`} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary"><Pencil className="w-3 h-3" /> Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); remove(p); }} data-testid={`delete-${p.id}`} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                {cfg.columns.map((c) => (
                  <th key={c} className="px-4 py-3 font-semibold">{cfg.labels[c]}</th>
                ))}
                {hasDocs && <th className="px-4 py-3 font-semibold">Documents</th>}
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const isMaint = moduleKey === "maintenance";
                return (
                <tr key={it.id} onClick={isMaint ? () => setDetailId(it.id) : undefined}
                  className={`border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${isMaint ? "cursor-pointer" : ""}`}
                  data-testid={`row-${it.id}`}>
                  {cfg.columns.map((c) => (
                    <td key={c} className="px-4 py-3">
                      {cfg.badge && cfg.badge.field === c ? renderBadge(it) : <span className={["amount", "monthly_rent", "market_value"].includes(c) ? "font-mono" : ""}>{fmt(c, it[c])}</span>}
                    </td>
                  ))}
                  {hasDocs && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <DocActions moduleKey={moduleKey} record={it} canWrite={canWrite} onChange={load} />
                    </td>
                  )}
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-3 items-center">
                      {isMaint && (
                        <button onClick={() => setDetailId(it.id)} data-testid={`open-${it.id}`} className="text-xs font-medium text-primary hover:underline">Open</button>
                      )}
                      {canWrite && (
                        <>
                          {showReminderAction && (
                            <>
                              <span className="text-xs text-muted-foreground self-center" data-testid={`last-reminded-${it.id}`}>
                                {remindedMap[it.tenant_id] ? `Reminded ${relTime(remindedMap[it.tenant_id])}` : "Not reminded"}
                              </span>
                              <button onClick={() => remindTenant(it)} disabled={remindingId === it.id} data-testid={`remind-${it.id}`} title={remindedMap[it.tenant_id] ? `Last reminded ${relTime(remindedMap[it.tenant_id])}` : "Send rent reminder to this tenant"} className="text-amber-600 hover:text-amber-700 disabled:opacity-50">
                                {remindingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                              </button>
                            </>
                          )}
                          <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => remove(it)} data-testid={`delete-${it.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={cfg.columns.length + (hasDocs ? 2 : 1)} className="px-4 py-12 text-center text-muted-foreground">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Maintenance detail modal */}
      {detailId && (
        <MaintenanceDetail
          itemId={detailId}
          canWrite={canWrite}
          onClose={() => { setDetailId(null); load(); }}
          onEdit={(rec) => { setDetailId(null); openEdit(rec); }}
          onChanged={load}
        />
      )}

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {cfg.singular}</DialogTitle>
            <DialogDescription>{editing ? "Update the" : "Create a new"} {cfg.singular.toLowerCase()} record.</DialogDescription>
          </DialogHeader>
          {moduleKey === "properties" && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="autofill-panel">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold">Auto-fill from public records</p>
              </div>
              <p className="text-xs text-muted-foreground">Enter the full property address and we'll pull units, type, value, beds/baths, year built and more from public records (RentCast). You can edit anything before saving.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="autofill-address-input"
                  placeholder="e.g. 5500 Grand Lake Dr, San Antonio, TX 78244"
                  value={lookupAddr}
                  onChange={(e) => setLookupAddr(e.target.value)}
                  className="rounded-xl flex-1"
                />
                <Button type="button" className="rounded-xl shrink-0" onClick={() => autoFill(false)} disabled={lookupLoading} data-testid="autofill-button">
                  {lookupLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {lookupLoading ? "Looking up..." : "Auto-fill"}
                </Button>
                <Button type="button" variant="outline" className="rounded-xl shrink-0" onClick={() => autoFill(true)} disabled={lookupLoading} data-testid="refresh-property-data" title="Re-run lookup, ignoring cached data">
                  <RefreshCw className={`w-4 h-4 ${lookupLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
              {(lookupInfo || form.photo) && (
                <div className="flex gap-3 pt-1">
                  {form.photo && <img src={form.photo} alt="Property" className="w-28 h-20 object-cover rounded-lg border border-border" data-testid="property-image-preview" />}
                  <div className="text-xs text-muted-foreground flex-1">
                    {form.market_value_source && <p>Value source: <span className="font-medium text-foreground">{form.market_value_source}</span></p>}
                    {lookupInfo?.missing_fields?.length > 0 && <p className="text-amber-600 mt-1">Missing (add manually): {lookupInfo.missing_fields.join(", ")}</p>}
                    {lookupInfo?.sources?.photo === "" && <p className="mt-1">No Street View image found — upload one manually below.</p>}
                  </div>
                </div>
              )}
              {lookupInfo && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs pt-1" data-testid="autofill-result">
                  {[["Type", lookupInfo.type], ["Units", lookupInfo.units],
                    ["Beds / Baths", `${lookupInfo.bedrooms ?? "—"} / ${lookupInfo.bathrooms ?? "—"}`],
                    ["Sq Ft", lookupInfo.square_footage ? lookupInfo.square_footage.toLocaleString() : "—"],
                    ["Year Built", lookupInfo.year_built ?? "—"],
                    ["Market Value", lookupInfo.market_value ? money(lookupInfo.market_value) : "—"],
                    ["Assessor Value", lookupInfo.assessor_value ? money(lookupInfo.assessor_value) : "—"],
                    ["Annual Tax", lookupInfo.property_tax ? money(lookupInfo.property_tax) : "—"],
                    ["Last Sale", lookupInfo.last_sale_price ? `${money(lookupInfo.last_sale_price)}${lookupInfo.last_sale_date ? " · " + lookupInfo.last_sale_date : ""}` : "—"],
                    ["Est. Rent", lookupInfo.rent_estimate ? `${money(lookupInfo.rent_estimate)}/mo` : "—"],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-lg bg-background/70 p-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
                      <p className="font-medium truncate">{val ?? "—"}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {moduleKey === "units" && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="rent-estimate-panel">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold">Comparable market rent</p>
              </div>
              <p className="text-xs text-muted-foreground">Pull a market rent estimate for this unit's property from comparable rentals (RentCast). Select the property first.</p>
              <Button type="button" variant="outline" className="rounded-xl" onClick={estimateRent} disabled={rentEstLoading} data-testid="rent-estimate-button">
                {rentEstLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {rentEstLoading ? "Estimating..." : "Estimate market rent"}
              </Button>
              {rentEst && (
                <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="rent-estimate-result">
                  <div className="rounded-lg bg-background/70 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Estimate</p>
                    <p className="font-heading font-bold text-primary">{money(rentEst.rent)}/mo</p>
                  </div>
                  <div className="rounded-lg bg-background/70 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Range</p>
                    <p className="font-medium">{money(rentEst.rent_low)}–{money(rentEst.rent_high)}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{rentEst.comparables_count} comps</span>
                  <Button type="button" size="sm" className="rounded-xl ml-auto" onClick={() => setForm((p) => ({ ...p, rent_amount: rentEst.rent }))} data-testid="rent-estimate-apply">
                    Use this rent
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {cfg.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "section" ? "sm:col-span-2" : ""}>
                {f.type === "section" ? (
                  <div className="pt-2 mt-1 border-t border-border">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">{f.label}</p>
                  </div>
                ) : (<>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">{f.label}{f.required && " *"}</label>
                {f.type === "textarea" ? (
                  <Textarea data-testid={`field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setField(f, e.target.value)} className="rounded-xl" />
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setField(f, v)}>
                    <SelectTrigger className="rounded-xl" data-testid={`field-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "ref" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setField(f, v)}>
                    <SelectTrigger className="rounded-xl" data-testid={`field-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{(refs[f.ref] || []).map((o) => <SelectItem key={o.id} value={o[f.refLabel]}>{o[f.refLabel]}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`field-${f.key}`}
                    type={f.type === "number" || f.type === "money" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => (moduleKey === "properties" && f.key === "units")
                      ? onUnitsChange(e.target.value)
                      : setField(f, f.type === "number" || f.type === "money" ? Number(e.target.value) : e.target.value)}
                    className="rounded-xl"
                  />
                )}
                </>)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="save-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
